import { DashboardStats } from '@/hooks/useDashboardData';
import { Plus, ShieldCheck, AlertTriangle, CircleDot, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DashboardHeaderProps {
  userName: string | null;
  stats: DashboardStats;
  onNewDecision: () => void;
}

export const DashboardHeader = ({ userName, stats, onNewDecision }: DashboardHeaderProps) => {
  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-serif font-semibold text-foreground tracking-tight">
            Aktive Entscheidungsverantwortung
          </h1>
          {userName && (
            <p className="text-sm text-muted-foreground mt-1">
              {userName}
            </p>
          )}
        </div>
        <Button
          onClick={onNewDecision}
          size="sm"
          className="shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all self-start sm:self-auto"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Neue Entscheidung
        </Button>
      </div>

      {/* Commitment KPIs - 4 columns */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {/* Braucht Aufmerksamkeit (primary action KPI) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative bg-card border border-primary/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 overflow-hidden cursor-default">
              <div className="absolute bottom-0 left-0 h-[3px] w-full bg-gradient-to-r from-primary to-primary/30" aria-hidden="true" />
              <div className="flex flex-col gap-1 sm:gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                  </div>
                </div>
                <span className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">{stats.needsAttention}</span>
                <span className="text-[10px] sm:text-xs text-primary font-medium leading-tight">Braucht Aufmerksamkeit</span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="text-xs">Sessions die &gt;14 Tage stagnieren oder in Phase 3 seit &gt;7 Tagen inaktiv sind.</p>
          </TooltipContent>
        </Tooltip>

        {/* Ohne Commit */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative bg-card border border-destructive/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 overflow-hidden cursor-default">
              <div className="absolute bottom-0 left-0 h-[3px] w-full bg-gradient-to-r from-destructive to-destructive/30" aria-hidden="true" />
              <div className="flex flex-col gap-1 sm:gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <CircleDot className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-destructive" />
                  </div>
                </div>
                <span className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">{stats.withoutCommit}</span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight">Ohne Commit</span>
              </div>
              {/* Stale breakdown mini-bar */}
              {(stats.stale_7 > 0 || stats.stale_14 > 0 || stats.overdue > 0) && (
                <div className="flex items-center gap-1 mt-1.5">
                  {stats.stale_7 > 0 && (
                    <span className="text-[9px] text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1 rounded">{stats.stale_7} &gt;7d</span>
                  )}
                  {stats.stale_14 > 0 && (
                    <span className="text-[9px] text-orange-600 dark:text-orange-400 bg-orange-500/10 px-1 rounded">{stats.stale_14} &gt;14d</span>
                  )}
                  {stats.overdue > 0 && (
                    <span className="text-[9px] text-destructive bg-destructive/10 px-1 rounded">{stats.overdue} &gt;30d</span>
                  )}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="text-xs">Aktive Entscheidungen ohne finalen Commit. Aufgeschlüsselt nach Inaktivitätsdauer.</p>
          </TooltipContent>
        </Tooltip>

        {/* Überfällig (>30d) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative bg-card border border-amber-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 overflow-hidden cursor-default">
              <div className="absolute bottom-0 left-0 h-[3px] w-full bg-gradient-to-r from-amber-500 to-amber-500/30" aria-hidden="true" />
              <div className="flex flex-col gap-1 sm:gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500" />
                  </div>
                </div>
                <span className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">{stats.overdue}</span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight">Überfällig &gt;30d</span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="text-xs">Seit über 30 Tagen keine Aktivität. Diese Entscheidungen sollten abgeschlossen oder archiviert werden.</p>
          </TooltipContent>
        </Tooltip>

        {/* GO / Binding (green) */}
        <div className="relative bg-card border border-emerald-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 overflow-hidden">
          <div className="absolute bottom-0 left-0 h-[3px] w-full bg-gradient-to-r from-emerald-500 to-emerald-500/30" aria-hidden="true" />
          <div className="flex flex-col gap-1 sm:gap-1.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
              </div>
            </div>
            <span className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">{stats.withBinding}</span>
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight">GO / Binding</span>
          </div>
        </div>
      </div>
    </div>
  );
};
